import { Autocomplete, AutocompleteInputChangeReason, AutocompleteProps } from "@mui/material";
import { FocusEvent, SyntheticEvent, forwardRef, useState } from "react";

import { defaultGetOptionLabel, handleAutocompleteBlurMatch } from "./autocomplete-blur-utils";

export const BaseAutocomplete = forwardRef(function BaseAutocomplete<
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false
>(
  props: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  ref: React.Ref<HTMLDivElement>
) {
  const {
    onBlur,
    onChange,
    onInputChange,
    inputValue: inputValueProp,
    options = [],
    value,
    getOptionLabel = defaultGetOptionLabel,
    isOptionEqualToValue,
    multiple,
    freeSolo,
    ...rest
  } = props;

  const [uncontrolledInputValue, setUncontrolledInputValue] = useState("");
  const managesInputValue = Boolean(multiple);
  const inputValue = managesInputValue
    ? inputValueProp ?? uncontrolledInputValue
    : inputValueProp;

  const clearInputValue = (event: SyntheticEvent) => {
    if (inputValueProp === undefined) {
      setUncontrolledInputValue("");
    }
    onInputChange?.(event, "", "reset");
  };

  const handleInputChange = (
    event: SyntheticEvent,
    newInputValue: string,
    reason: AutocompleteInputChangeReason
  ) => {
    if (managesInputValue && inputValueProp === undefined) {
      setUncontrolledInputValue(newInputValue);
    }
    onInputChange?.(event, newInputValue, reason);
  };

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    const valueApplied = handleAutocompleteBlurMatch(
      event,
      {
        options,
        value,
        multiple,
        freeSolo,
        getOptionLabel,
        isOptionEqualToValue,
      },
      onChange
    );

    if (valueApplied && multiple) {
      clearInputValue(event);
    }

    onBlur?.(event);
  };

  return (
    <Autocomplete
      ref={ref}
      {...rest}
      multiple={multiple}
      freeSolo={freeSolo}
      options={options}
      value={value}
      {...(managesInputValue
        ? {
            inputValue,
            onInputChange: handleInputChange,
          }
        : {
            ...(inputValueProp !== undefined ? { inputValue: inputValueProp } : {}),
            ...(onInputChange ? { onInputChange } : {}),
          })}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      onChange={onChange}
      onBlur={handleBlur}
    />
  );
}) as <
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false
>(
  props: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> & {
    ref?: React.Ref<HTMLDivElement>;
  }
) => React.JSX.Element;
